import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { ChevronDown, ChevronUp } from 'lucide-react'

import styles from './SeatBreakdown.module.scss'

import { Spinner } from '@/components/Spinner'
import { useAnalytics } from '@/hooks/useAnalytics'
import { RootState } from '@/store'
import {
  selectIsMembersLoading,
  selectItAdmins,
  selectMembers,
  selectPaidSeats,
  selectTotalPendingInvites,
  selectTrialingMembers,
} from '@/store/features/member/memberSlice'
import { selectCurrentEnterprise } from '@/store/features/user/userSlice'
import { ACTIVE_ENTERPRISE_SUBSCRIPTION_STATUSES, Member, SubscriptionStatus } from '@/types'
import { EventName } from '@/utils/eventDefinitions'

const SeatBreakdown = () => {
  const [expanded, setExpanded] = useState(false)
  const { trackEvent } = useAnalytics()

  const paidCount = useSelector(selectPaidSeats)
  const trialingCount = useSelector(selectTrialingMembers)
  const itAdminCount = useSelector(selectItAdmins)
  const invitedCount = useSelector(selectTotalPendingInvites)
  const isLoading = useSelector(selectIsMembersLoading)
  const hasLoaded = useSelector((state: RootState) => state.member.hasLoaded)
  const subscription = useSelector(selectCurrentEnterprise)?.subscription
  const members = useSelector(selectMembers)
  const joinedThisMonthCount = useMemo(() => {
    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime() / 1000
    return members.filter((m) => {
      const joinedAt = (m as Member & { joinedAt?: number | null }).joinedAt
      return joinedAt != null && joinedAt >= startOfMonth
    }).length
  }, [members])

  if (subscription && !ACTIVE_ENTERPRISE_SUBSCRIPTION_STATUSES.includes(subscription.status ?? '')) {
    return <span className={styles.inactiveMessage}>Your subscription is inactive, please reactivate</span>
  }

  if (isLoading || !hasLoaded) {
    return (
      <div className={styles.root}>
        <Spinner size={20} color="#656067" />
      </div>
    )
  }

  // Headline derives from the four buckets so it can never drift from the
  // breakdown — the buckets are MECE+exhaustive over members + invites.
  const memberCount = paidCount + trialingCount + itAdminCount + invitedCount
  const seatsCurrentCycle = subscription?.seatsCurrentCycle || subscription?.numSeats || 0
  // When the whole subscription is on trial, no seats are billed yet.
  const billedSeatsTotal =
    subscription?.status === SubscriptionStatus.Trialing ? 0 : Math.max(seatsCurrentCycle, paidCount)

  const toggle = () => {
    const next = !expanded
    setExpanded(next)
    trackEvent?.(EventName.SeatBreakdownToggled, { expanded: next })
  }

  return (
    <div className={styles.root}>
      <div className={styles.headerLine}>
        <span className={styles.headerText}>
          <strong>
            {paidCount} of {billedSeatsTotal} billed seats
          </strong>{' '}
          in use across <strong>{memberCount} members</strong>.
        </span>
        <button type="button" className={styles.toggle} onClick={toggle} aria-expanded={expanded}>
          {expanded ? 'Hide breakdown' : 'Show breakdown'}
          {expanded ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
        </button>
        {joinedThisMonthCount > 0 && (
          <span className={styles.joinedThisMonth}>+{joinedThisMonthCount} joined this month</span>
        )}
      </div>

      {expanded && (
        <div className={styles.breakdown} role="region" aria-label="Seat breakdown">
          <div className={styles.bucket}>
            <div className={styles.count}>{paidCount}</div>
            <div className={styles.label}>Paid seats</div>
            <div className={styles.caption}>Active, billed</div>
          </div>
          <div className={styles.bucket}>
            <div className={styles.count}>{trialingCount}</div>
            <div className={styles.label}>Trialing</div>
            <div className={styles.caption}>Free for 2 weeks, then billed</div>
          </div>
          <div className={styles.bucket}>
            <div className={styles.count}>{invitedCount}</div>
            <div className={styles.label}>Invited</div>
            <div className={styles.caption}>Counts when accepted</div>
          </div>
          <div className={styles.bucket}>
            <div className={styles.count}>{itAdminCount}</div>
            <div className={styles.label}>IT admin</div>
            <div className={styles.caption}>Always free</div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SeatBreakdown
